import { ChevronRight, Plus, Search } from 'lucide-react'
import { Button } from '@/common/ui/button'
import { LabelInput } from '@/common/ui/LabelInput'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/common/ui/table'
import { NAVIGATION_COPY } from '@/locales/en'
import { AccountType } from '@/common/types'
import { formatDisplayName } from '../utils/formatDisplayName'

type AccountInfo = {
    accountId: string
    accountName: string
    accountType: AccountType
}

export type ClientsTableProps = {
    searchTerm?: string
    onSearchChange?: (value: string) => void
    filteredAccounts?: AccountInfo[]
    activeAccountId?: string | null
    hasClientAccounts?: boolean
    clientCountLabel?: string
    showClientCount?: boolean
    onAddClient?: () => void
    onSelectAccount?: (accountId: string) => void
}

export function ClientsTable({
    searchTerm = '',
    onSearchChange = () => {},
    filteredAccounts = [],
    activeAccountId = null,
    hasClientAccounts = false,
    clientCountLabel = '',
    showClientCount = false,
    onAddClient = () => {},
    onSelectAccount = () => {},
}: ClientsTableProps) {
    const hasSearchResults = filteredAccounts.length > 0
    const renderAddClientButton = ({ disabled = false, className = '' } = {}) => (
        <Button
            variant="secondary"
            size="lg"
            aria-label="Add client"
            className={`px-6 py-3 ${className}`}
            disabled={disabled}
            onClick={disabled ? undefined : onAddClient}
        >
            <span>{NAVIGATION_COPY.adviserClients.addClient}</span>
            <Plus size={16} />
        </Button>
    )

    if (!hasClientAccounts) {
        return (
            <div className="w-full bg-white rounded-tl-lg rounded-tr-lg flex flex-col gap-8">
                <div className="self-stretch flex flex-col gap-6">
                    <div className="self-stretch inline-flex flex-col justify-center items-start gap-6">
                        <div className="flex flex-col justify-start items-start gap-4">
                            <div className="rounded-md inline-flex justify-center items-center gap-2.5">
                                <div className="justify-start text-black text-3xl font-normal font-['Serious Sans'] leading-10">
                                    {NAVIGATION_COPY.adviserClients.addFirstClient}
                                </div>
                            </div>
                            <div className="rounded-md inline-flex justify-center items-center gap-2.5">
                                <div className="justify-start text-black text-base font-normal font-['Inter'] leading-6">
                                    {NAVIGATION_COPY.adviserClients.addFirstClientDescription}
                                </div>
                            </div>
                        </div>
                        {renderAddClientButton({ className: 'self-stretch sm:self-auto' })}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="w-full bg-white rounded-tl-lg rounded-tr-lg flex flex-col gap-8">
            <div className="self-stretch flex flex-col gap-6">
                <div className="self-stretch flex items-center gap-4">
                    <div className="w-full max-w-xl">
                        <div className="relative w-full">
                            <div className="pointer-events-none absolute inset-y-0 left-4 flex items-center">
                                <Search size={16} className="text-brand-primary-forest" />
                            </div>
                            <LabelInput
                                id="client-search"
                                label={NAVIGATION_COPY.adviserClients.searchLabel}
                                labelClassName="sr-only"
                                className="pl-10 bg-white border-neutral-300 text-sm text-black placeholder:text-content-secondary"
                                value={searchTerm}
                                onChange={(event) => onSearchChange(event.target.value)}
                                placeholder={NAVIGATION_COPY.adviserClients.searchPlaceholder}
                            />
                        </div>
                    </div>
                </div>

                {showClientCount && hasSearchResults && (
                    <div className="flex items-center justify-between gap-6">
                        <div className="text-black text-sm font-medium leading-5">{clientCountLabel}</div>
                        {renderAddClientButton()}
                    </div>
                )}

                {!hasSearchResults ? (
                    <div className="flex items-center justify-between gap-6">
                        <div className="self-stretch flex flex-col items-start">
                            <div className="justify-start text-black text-sm font-medium font-['Inter'] leading-5">
                                {NAVIGATION_COPY.adviserClients.noClientsFound}
                            </div>
                            {searchTerm && (
                                <div className="justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                                    {NAVIGATION_COPY.adviserClients.tryChangingSearchTerms}
                                </div>
                            )}
                        </div>
                        {renderAddClientButton()}
                    </div>
                ) : (
                    <>
                        <div className="self-stretch bg-white flex flex-col overflow-hidden rounded-lg md:border border-stone-200 border-t-0 border-b">
                            <Table className="min-w-full">
                                <TableHeader className="md:bg-stone-50">
                                    <TableRow className="hover:bg-stone-50">
                                        <TableHead className="px-2 md:px-6 py-3 text-xs font-medium text-content-secondary md:hidden">
                                            {NAVIGATION_COPY.adviserClients.accountName}
                                        </TableHead>
                                        <TableHead className="hidden md:table-cell px-2 md:px-6 py-3 text-xs font-medium text-content-secondary">
                                            {NAVIGATION_COPY.adviserClients.firstName}
                                        </TableHead>
                                        <TableHead className="hidden md:table-cell px-2 md:px-6 py-3 text-xs font-medium text-content-secondary">
                                            {NAVIGATION_COPY.adviserClients.lastName}
                                        </TableHead>
                                        <TableHead className="px-2 md:px-6 py-3 text-xs font-medium text-content-secondary text-right">
                                            {NAVIGATION_COPY.adviserClients.id}
                                        </TableHead>
                                        <TableHead className="px-2 md:px-6 py-3 text-xs font-medium text-content-secondary">
                                            {NAVIGATION_COPY.adviserClients.type}
                                        </TableHead>
                                        <TableHead className="w-12 px-2 md:px-6 py-3 text-xs font-medium text-content-secondary">
                                            <span className="sr-only">
                                                {NAVIGATION_COPY.adviserClients.selectAccount}
                                            </span>
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredAccounts.map((currentAccount) => {
                                        const isActive = currentAccount.accountId === activeAccountId
                                        const { firstName, lastName } = formatDisplayName(currentAccount.accountName)

                                        return (
                                            <TableRow
                                                key={currentAccount.accountId}
                                                className={`border-b md:border-stone-200 ${isActive ? 'bg-brand-accent-soft-lime/25' : 'bg-white'} cursor-pointer hover:bg-stone-50`}
                                                onClick={() => onSelectAccount(currentAccount.accountId)}
                                                onKeyDown={(event) => {
                                                    if (event.key !== 'Enter' && event.key !== ' ') return
                                                    event.preventDefault()
                                                    onSelectAccount(currentAccount.accountId)
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                aria-label={`Switch to ${currentAccount.accountName}`}
                                                data-state={isActive ? 'selected' : undefined}
                                            >
                                                <TableCell className="px-2 md:px-6 py-4 text-left text-black text-sm font-medium leading-5">
                                                    <span className="md:hidden">{currentAccount.accountName}</span>
                                                    <span className="hidden md:inline">{firstName}</span>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell px-2 md:px-6 py-4 text-sm font-normal text-black leading-5">
                                                    {lastName}
                                                </TableCell>
                                                <TableCell className="md:px-6 py-4 text-right text-sm font-normal text-black leading-5">
                                                    {currentAccount.accountId}
                                                </TableCell>
                                                <TableCell className="px-2 md:px-6 py-4 text-sm font-normal text-black leading-5">
                                                    {NAVIGATION_COPY.accountTypes[currentAccount.accountType]}
                                                </TableCell>
                                                <TableCell className="px-2 md:px-6 py-4 text-right">
                                                    <ChevronRight size={16} className="text-content-secondary" />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
